import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  UploadCloud, Save, Image as ImageIcon, Monitor, Smartphone, RotateCcw,
  Sparkles, Type, Palette, Link2, Check, X, AlertCircle, Trash2,
  Move, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react'
import {
  fetchPromoBanner,
  savePromoBanner,
  clearPromoBannerError,
} from './services/promoBannerSlice'

/* Empty preview only (no image yet). Uploaded images can be ANY size. */
const PLACEHOLDER_RATIO = '1906 / 444'
const MOBILE_FRAME_WIDTH = 375

const MAX_TEXT = 300
const MAX_FILE_MB = 5

/* ── Text layout ──
   x, y  = centre of the text block, in % of the banner (0-100)
   size  = font size, in % of the banner width
   align = left | center | right
   (same defaults + same units as the backend / website) */
const DEFAULT_LAYOUT = {
  desktop: {
    left: { x: 17.5, y: 44, size: 3, align: 'left' },
    right: { x: 83, y: 44, size: 3, align: 'center' },
  },
  mobile: {
    left: { x: 26, y: 30, size: 4.2, align: 'left' },
    right: { x: 74, y: 30, size: 4.2, align: 'center' },
  },
}
const SNAP = 1.5

const clamp = (n, min, max) => Math.max(min, Math.min(max, n))
const round1 = (n) => Math.round(n * 10) / 10

const normalizeLayout = (raw) => {
  const out = {}
  Object.keys(DEFAULT_LAYOUT).forEach((device) => {
    out[device] = {}
    Object.keys(DEFAULT_LAYOUT[device]).forEach((name) => {
      const def = DEFAULT_LAYOUT[device][name]
      const inp = raw?.[device]?.[name] || {}
      const num = (k, min, max) => (Number.isFinite(Number(inp[k])) && inp[k] !== null && inp[k] !== ''
        ? clamp(Number(inp[k]), min, max) : def[k])
      out[device][name] = {
        x: num('x', 0, 100),
        y: num('y', 0, 100),
        size: num('size', 1, 10),
        align: ['left', 'center', 'right'].includes(inp.align) ? inp.align : def.align,
      }
    })
  })
  return out
}

const EMPTY = {
  left_text: 'Free [[SHIPPING]] on\norder above [[₹500]]',
  right_text: 'Get [[₹100 OFF]]\non your [[First Order]]',
  link: '',
  status: 1,
  bg_color: '#2F5130',
  text_color: '#FFFFFF',
  highlight_color: '#D2AE4F',
  layout: normalizeLayout(null),
}

const formFromSaved = (saved) =>
  saved
    ? {
        left_text: saved.left_text || '',
        right_text: saved.right_text || '',
        link: saved.link || '',
        status: saved.status,
        bg_color: saved.bg_color || EMPTY.bg_color,
        text_color: saved.text_color || EMPTY.text_color,
        highlight_color: saved.highlight_color || EMPTY.highlight_color,
        layout: normalizeLayout(saved.layout),
      }
    : { ...EMPTY, layout: normalizeLayout(null) }

const formatBytes = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : `${Math.round(bytes / 1024)} KB`

/* "[[gold]] normal" + line breaks -> React nodes (same output as the site's PromoText::html) */
function renderPromoText(text) {
  return String(text || '').split('\n').map((line, i, arr) => (
    <span key={i}>
      {line.split(/(\[\[.+?\]\])/g).map((part, j) =>
        /^\[\[.+\]\]$/.test(part)
          ? <span key={j} className="pbn-hl">{part.slice(2, -2)}</span>
          : <span key={j}>{part}</span>
      )}
      {i < arr.length - 1 && <br />}
    </span>
  ))
}

/* Validate a picked/dropped file (type + file size only — any image dimensions are allowed). */
function validateImage(file, setError, onPick) {
  if (!file) return
  if (!file.type.startsWith('image/')) return setError('Please select an image file (JPG, PNG or WEBP).')
  if (file.size > MAX_FILE_MB * 1024 * 1024) return setError(`The image must be smaller than ${MAX_FILE_MB} MB.`)
  setError('')
  onPick(file)
}

/* ── Drag & drop image upload ── */
function Dropzone({ title, hint, chips, url, file, error, onFile, onClear, extra }) {
  const inputRef = useRef(null)
  const [over, setOver] = useState(false)

  const handleDrop = (e) => {
    e.preventDefault()
    setOver(false)
    onFile(e.dataTransfer.files?.[0])
  }

  return (
    <div className="pbn-field">
      <div className="pbn-label-row">
        <span className="pbn-label">{title}</span>
        <div className="pbn-chips">
          {chips.map((c) => <span key={c} className="pbn-chip">{c}</span>)}
        </div>
      </div>

      <input
        ref={inputRef} type="file" hidden accept="image/png,image/jpeg,image/webp"
        onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
      />

      <div
        className={`pbn-drop ${over ? 'is-over' : ''} ${error ? 'has-error' : ''}`}
        role="button" tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click() } }}
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
      >
        {url ? (
          <div className="pbn-drop-thumb">
            <img src={url} alt="" />
            {file && <span className="pbn-new-tag">New</span>}
          </div>
        ) : (
          <div className="pbn-drop-empty">
            <span className="pbn-drop-icon"><UploadCloud size={20} /></span>
            <b>Click to upload</b> or drag and drop
            <small>{hint}</small>
          </div>
        )}
      </div>

      <div className="pbn-file-row">
        {file ? (
          <>
            <span className="pbn-file-name" title={file.name}>{file.name}</span>
            <span className="pbn-file-size">{formatBytes(file.size)}</span>
            <button type="button" className="pbn-link-btn" onClick={onClear}><X size={12} /> Discard</button>
          </>
        ) : (
          <button type="button" className="pbn-link-btn" onClick={() => inputRef.current?.click()}>
            <UploadCloud size={12} /> {url ? 'Replace image' : 'Choose image'}
          </button>
        )}
        {extra}
      </div>

      {error && <p className="pbn-error"><AlertCircle size={13} /> {error}</p>}
    </div>
  )
}

/* ── Offer text field with a highlight helper ── */
function OfferText({ label, value, onChange, error, textareaRef }) {
  const highlight = () => {
    const el = textareaRef.current
    if (!el) return
    const { selectionStart: s, selectionEnd: e } = el
    if (s === e) { el.focus(); return }
    const selected = value.slice(s, e)
    if (/^\[\[.*\]\]$/.test(selected)) return
    onChange(`${value.slice(0, s)}[[${selected}]]${value.slice(e)}`)
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s, e + 4) })
  }

  return (
    <div className="pbn-field">
      <div className="pbn-label-row">
        <span className="pbn-label">{label}</span>
        <button type="button" className="pbn-link-btn" onClick={highlight} title="Select some text first, then click">
          <Sparkles size={12} /> Highlight selection
        </button>
      </div>
      <textarea
        ref={textareaRef} className="pbn-input pbn-textarea" rows={3} maxLength={MAX_TEXT}
        value={value} onChange={(e) => onChange(e.target.value)}
      />
      <div className="pbn-label-row">
        {error ? <p className="pbn-error"><AlertCircle size={13} /> {error}</p> : <span />}
        <small className="pbn-muted">{value.length}/{MAX_TEXT}</small>
      </div>
    </div>
  )
}

/* ── Color field: swatch + hex ── */
function ColorField({ label, hint, value, onChange }) {
  return (
    <div className="pbn-field">
      <span className="pbn-label">{label}</span>
      <div className="pbn-color">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={label} />
        <input
          className="pbn-input pbn-hex" value={value} maxLength={7} spellCheck={false}
          onChange={(e) => {
            const v = e.target.value.toUpperCase()
            if (/^#[0-9A-F]{0,6}$/.test(v)) onChange(v)
          }}
        />
      </div>
      {hint && <small className="pbn-muted">{hint}</small>}
    </div>
  )
}

/* ── Position / size / alignment controls for one text block ── */
function BlockControls({ title, cfg, selected, onSelect, onChange, onReset }) {
  return (
    <div className={`pbn-blk ${selected ? 'is-selected' : ''}`} onClick={onSelect}>
      <div className="pbn-label-row">
        <span className="pbn-label">{title}</span>
        <button type="button" className="pbn-link-btn" onClick={(e) => { e.stopPropagation(); onReset() }}>
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="pbn-blk-row">
        <span className="pbn-blk-lbl">Size</span>
        <input
          type="range" min="1" max="8" step="0.1" value={cfg.size}
          onChange={(e) => onChange({ size: round1(Number(e.target.value)) })}
          aria-label={`${title} size`}
        />
        <span className="pbn-blk-val">{cfg.size}</span>
      </div>

      <div className="pbn-blk-row">
        <span className="pbn-blk-lbl">Align</span>
        <div className="pbn-seg">
          {[['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]].map(([a, Icon]) => (
            <button
              key={a} type="button" className={cfg.align === a ? 'is-on' : ''}
              onClick={() => onChange({ align: a })} aria-label={`Align ${a}`}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>

      <div className="pbn-blk-row">
        <span className="pbn-blk-lbl">Position</span>
        <label className="pbn-xy">X
          <input
            type="number" min="0" max="100" step="0.5" value={cfg.x}
            onChange={(e) => onChange({ x: clamp(Number(e.target.value) || 0, 0, 100) })}
          />%
        </label>
        <label className="pbn-xy">Y
          <input
            type="number" min="0" max="100" step="0.5" value={cfg.y}
            onChange={(e) => onChange({ y: clamp(Number(e.target.value) || 0, 0, 100) })}
          />%
        </label>
      </div>
    </div>
  )
}

export default function PromoBanner() {
  const dispatch = useDispatch()
  const { data: saved, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.promoBanner)

  const [form, setForm] = useState(EMPTY)
  const [image, setImage] = useState(null)          // new desktop File
  const [mobile, setMobile] = useState(null)        // new mobile File
  const [removeMobile, setRemoveMobile] = useState(false)
  const [imgErr, setImgErr] = useState('')
  const [mobErr, setMobErr] = useState('')
  const [notice, setNotice] = useState('')
  const [device, setDevice] = useState('desktop')   // preview + layout editing mode
  const [selected, setSelected] = useState('left')  // which text block is selected
  const [dragging, setDragging] = useState(null)    // 'left' | 'right' | null

  const stageRef = useRef(null)
  const dragOffset = useRef({ dx: 0, dy: 0 })
  const leftRef = useRef(null)
  const rightRef = useRef(null)

  const imagePreview = useMemo(() => (image ? URL.createObjectURL(image) : null), [image])
  const mobilePreview = useMemo(() => (mobile ? URL.createObjectURL(mobile) : null), [mobile])
  useEffect(() => () => { if (imagePreview) URL.revokeObjectURL(imagePreview) }, [imagePreview])
  useEffect(() => () => { if (mobilePreview) URL.revokeObjectURL(mobilePreview) }, [mobilePreview])

  /* Load */
  useEffect(() => {
    dispatch(clearPromoBannerError())
    dispatch(fetchPromoBanner())
  }, [dispatch])

  /* Sync the form whenever the saved data changes (after load / save) */
  useEffect(() => {
    setImage(null); setMobile(null); setRemoveMobile(false)
    setImgErr(''); setMobErr('')
    setForm(formFromSaved(saved))
  }, [saved])

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  /* Update one text block of the device being edited */
  const setBlock = (name, patch, dev = device) =>
    setForm((f) => ({
      ...f,
      layout: { ...f.layout, [dev]: { ...f.layout[dev], [name]: { ...f.layout[dev][name], ...patch } } },
    }))

  const resetBlock = (name) => setBlock(name, { ...DEFAULT_LAYOUT[device][name] })

  const baseline = useMemo(() => formFromSaved(saved), [saved])
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline) || !!image || !!mobile || removeMobile,
    [form, baseline, image, mobile, removeMobile]
  )

  const reset = () => {
    setForm(baseline)
    setImage(null); setMobile(null); setRemoveMobile(false)
    setImgErr(''); setMobErr(''); setNotice('')
    dispatch(clearPromoBannerError())
  }

  const save = async () => {
    setNotice(''); setImgErr(''); setMobErr('')
    dispatch(clearPromoBannerError())

    if (!saved && !image) { setImgErr('Please upload a banner image.'); return }

    const fd = new FormData()
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'layout') fd.append('layout', JSON.stringify(v))
      else fd.append(k, v ?? '')
    })
    if (image) fd.append('image', image)
    if (mobile) fd.append('mobile_image', mobile)
    fd.append('remove_mobile', removeMobile ? 1 : 0)

    const result = await dispatch(savePromoBanner(fd))
    if (result.meta.requestStatus === 'fulfilled') {
      setNotice('Your promo banner has been saved and is now live on the home page.')
    }
  }

  /* ── Drag & drop of the text blocks on the preview ── */
  const pointerPct = (e) => {
    const rect = stageRef.current.getBoundingClientRect()
    return {
      px: ((e.clientX - rect.left) / rect.width) * 100,
      py: ((e.clientY - rect.top) / rect.height) * 100,
    }
  }

  const onBlockDown = (e, name) => {
    if (!stageRef.current) return
    e.preventDefault()
    const { px, py } = pointerPct(e)
    const cfg = form.layout[device][name]
    dragOffset.current = { dx: cfg.x - px, dy: cfg.y - py }
    e.currentTarget.setPointerCapture(e.pointerId)
    setSelected(name)
    setDragging(name)
  }

  const onBlockMove = (e, name) => {
    if (dragging !== name || !stageRef.current) return
    const { px, py } = pointerPct(e)
    let x = clamp(px + dragOffset.current.dx, 0, 100)
    let y = clamp(py + dragOffset.current.dy, 0, 100)
    if (Math.abs(x - 50) < SNAP) x = 50          // snap to the centre lines
    if (Math.abs(y - 50) < SNAP) y = 50
    setBlock(name, { x: round1(x), y: round1(y) })
  }

  const onBlockUp = (e) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    setDragging(null)
  }

  const onBlockKey = (e, name) => {
    const step = e.shiftKey ? 2 : 0.5
    const cfg = form.layout[device][name]
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }
    const m = moves[e.key]
    if (!m) return
    e.preventDefault()
    setBlock(name, { x: round1(clamp(cfg.x + m[0], 0, 100)), y: round1(clamp(cfg.y + m[1], 0, 100)) })
  }

  const desktopUrl = imagePreview || saved?.image || null
  const mobileUrl = mobilePreview || (!removeMobile ? saved?.mobile_image : null)
  const isActive = Number(form.status) === 1
  const stageImage = device === 'desktop' ? desktopUrl : (mobileUrl || desktopUrl)

  if (isLoading && !saved) return <div className="dash-page page-in">Loading…</div>

  const cssVars = { '--pb-text': form.text_color, '--pb-hl': form.highlight_color }
  const blocks = [
    ['left', 'Left offer', form.left_text],
    ['right', 'Right offer', form.right_text],
  ]

  const stage = (
    <div
      ref={stageRef}
      className={`pbn-stage ${device === 'mobile' ? 'is-mobile' : ''}`}
      style={{
        ...cssVars,
        ...(stageImage ? {} : { aspectRatio: device === 'mobile' ? '3 / 2' : PLACEHOLDER_RATIO }),
        ...(device === 'mobile' ? { width: MOBILE_FRAME_WIDTH } : {}),
      }}
    >
      {stageImage
        ? <img src={stageImage} alt="Promo banner preview" draggable={false} />
        : <div className="pbn-stage-empty"><ImageIcon size={26} /><span>Upload a banner image to see the preview</span></div>}

      {dragging && (<><span className="pbn-guide pbn-guide-v" /><span className="pbn-guide pbn-guide-h" /></>)}

      {blocks.map(([name, , text]) => {
        const cfg = form.layout[device][name]
        return (
          <div
            key={name}
            className={`pbn-block ${selected === name ? 'is-selected' : ''} ${dragging === name ? 'is-dragging' : ''}`}
            style={{ left: `${cfg.x}%`, top: `${cfg.y}%`, fontSize: `${cfg.size}cqw`, textAlign: cfg.align }}
            tabIndex={0}
            role="button"
            aria-label={`Move ${name} offer text`}
            onPointerDown={(e) => onBlockDown(e, name)}
            onPointerMove={(e) => onBlockMove(e, name)}
            onPointerUp={onBlockUp}
            onPointerCancel={onBlockUp}
            onKeyDown={(e) => onBlockKey(e, name)}
            onFocus={() => setSelected(name)}
          >
            <div>{renderPromoText(text)}</div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="dash-page page-in pbn">
      {/* ── Header ── */}
      <div className="pbn-header">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Promo Banner</h1>
          <p className="pbn-sub">The offer banner shown on your home page. The image has no text — drag your offer text to the exact place you want, separately for desktop and mobile.</p>
        </div>
        <div className="pbn-header-actions">
          {dirty && <span className="pbn-dirty"><span className="pbn-dot" /> Unsaved changes</span>}
          <button className="btn btn-outline" onClick={reset} disabled={!dirty || isSaving}>
            <RotateCcw size={14} /> Reset
          </button>
          <button className="btn btn-primary" onClick={save} disabled={isSaving || !dirty}>
            <Save size={14} /> {isSaving ? 'Saving…' : 'Save banner'}
          </button>
        </div>
      </div>

      {notice && <div className="pbn-alert pbn-alert--ok"><Check size={15} /> {notice}</div>}
      {isError && message && <div className="pbn-alert pbn-alert--err"><AlertCircle size={15} /> {message}</div>}

      {/* ── Live preview + drag & drop ── */}
      <section className="card pbn-card">
        <div className="pbn-card-head">
          <div>
            <h2 className="pbn-title"><Move size={16} /> Drag &amp; drop preview</h2>
            <p className="pbn-muted">
              Drag the text on the image to place it. Arrow keys nudge it (hold Shift for bigger steps).
              Editing: <b>{device === 'desktop' ? 'Desktop' : 'Mobile'}</b> layout.
            </p>
          </div>
          <div className="pbn-tabs" role="tablist" aria-label="Preview device">
            <button role="tab" aria-selected={device === 'desktop'} className={device === 'desktop' ? 'is-on' : ''} onClick={() => setDevice('desktop')}>
              <Monitor size={14} /> Desktop
            </button>
            <button role="tab" aria-selected={device === 'mobile'} className={device === 'mobile' ? 'is-on' : ''} onClick={() => setDevice('mobile')}>
              <Smartphone size={14} /> Mobile
            </button>
          </div>
        </div>

        {device === 'desktop'
          ? stage
          : <div className="pbn-phone-bg">{stage}</div>}
      </section>

      <div className="pbn-grid">
        {/* ── Left column ── */}
        <div className="pbn-col">
          <section className="card pbn-card">
            <div className="pbn-card-head">
              <h2 className="pbn-title"><Move size={16} /> Text position — {device === 'desktop' ? 'Desktop' : 'Mobile'}</h2>
            </div>

            <div className="pbn-two">
              {blocks.map(([name, title]) => (
                <BlockControls
                  key={name}
                  title={title}
                  cfg={form.layout[device][name]}
                  selected={selected === name}
                  onSelect={() => setSelected(name)}
                  onChange={(patch) => setBlock(name, patch)}
                  onReset={() => resetBlock(name)}
                />
              ))}
            </div>
            <p className="pbn-note">
              Position is the centre of each text block, in % of the banner. Size is a % of the banner width, so the text
              scales with the banner on every screen. Switch the Desktop / Mobile tab above to set the other layout.
            </p>
          </section>

          <section className="card pbn-card">
            <div className="pbn-card-head">
              <h2 className="pbn-title"><ImageIcon size={16} /> Images</h2>
            </div>

            <Dropzone
              title="Desktop banner"
              hint={`JPG, PNG or WEBP · any size · max ${MAX_FILE_MB} MB`}
              chips={['Any size', 'No text on image']}
              url={desktopUrl}
              file={image}
              error={imgErr || errors?.image?.[0]}
              onFile={(f) => validateImage(f, setImgErr, setImage)}
              onClear={() => { setImage(null); setImgErr('') }}
            />
            <p className="pbn-note">Leave empty space on the image where the offer text will go.</p>

            <div className="pbn-divider" />

            <Dropzone
              title="Mobile banner (optional)"
              hint="If empty, the desktop image is used on phones"
              chips={['Any size']}
              url={mobileUrl}
              file={mobile}
              error={mobErr || errors?.mobile_image?.[0]}
              onFile={(f) => validateImage(f, setMobErr, (file) => { setMobile(file); setRemoveMobile(false) })}
              onClear={() => { setMobile(null); setMobErr('') }}
              extra={
                !mobile && saved?.mobile_image && (
                  removeMobile
                    ? <button type="button" className="pbn-link-btn" onClick={() => setRemoveMobile(false)}><RotateCcw size={12} /> Undo remove</button>
                    : <button type="button" className="pbn-link-btn pbn-danger" onClick={() => setRemoveMobile(true)}><Trash2 size={12} /> Remove</button>
                )
              }
            />
          </section>

          <section className="card pbn-card">
            <div className="pbn-card-head">
              <h2 className="pbn-title"><Type size={16} /> Offer text</h2>
            </div>

            <div className="pbn-two">
              <OfferText
                label="Left offer" value={form.left_text} textareaRef={leftRef}
                onChange={(v) => set('left_text', v)} error={errors?.left_text?.[0]}
              />
              <OfferText
                label="Right offer" value={form.right_text} textareaRef={rightRef}
                onChange={(v) => set('right_text', v)} error={errors?.right_text?.[0]}
              />
            </div>

            <p className="pbn-note">
              Each line break becomes a new line on the banner. To colour a word, wrap it in double brackets —
              for example <code>Free [[SHIPPING]] on</code> — or select the word and click “Highlight selection”.
            </p>
          </section>
        </div>

        {/* ── Right column ── */}
        <div className="pbn-col">
          <section className="card pbn-card">
            <div className="pbn-card-head">
              <h2 className="pbn-title"><Palette size={16} /> Appearance</h2>
            </div>
            <ColorField label="Text color" value={form.text_color} onChange={(v) => set('text_color', v)} />
            <ColorField label="Highlight color" hint="Used for words inside [[ ]]" value={form.highlight_color} onChange={(v) => set('highlight_color', v)} />
          </section>

          <section className="card pbn-card">
            <div className="pbn-card-head">
              <h2 className="pbn-title"><Link2 size={16} /> Settings</h2>
            </div>

            <div className="pbn-field">
              <span className="pbn-label">Click link <span className="pbn-muted">(optional)</span></span>
              <input
                className="pbn-input" value={form.link} placeholder="/shop/bags"
                onChange={(e) => set('link', e.target.value)}
              />
              <small className="pbn-muted">Where visitors go when they click the banner.</small>
              {errors?.link?.[0] && <p className="pbn-error"><AlertCircle size={13} /> {errors.link[0]}</p>}
            </div>

            <div className="pbn-switch-row">
              <div>
                <span className="pbn-label">Show banner on website</span>
                <small className="pbn-muted pbn-block">
                  {isActive ? 'Active — visible on the home page.' : 'Inactive — the default static banner is shown instead.'}
                </small>
              </div>
              <button
                type="button" role="switch" aria-checked={isActive}
                className={`pbn-switch ${isActive ? 'is-on' : ''}`}
                onClick={() => set('status', isActive ? 0 : 1)}
              >
                <span />
              </button>
            </div>
          </section>
        </div>
      </div>

      <style>{`
        .pbn { display: flex; flex-direction: column; gap: var(--space-lg, 20px); width: 100%; }
        .pbn code { background: var(--bg-raised, #f3f4f6); padding: 1px 6px; border-radius: 6px; font-size: 12px; }

        /* header */
        .pbn-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .pbn-sub { margin: 6px 0 0; color: var(--text3); font-size: var(--fs-sm, 13px); max-width: 620px; }
        .pbn-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pbn-dirty { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #F59E0B; font-weight: 600; }
        .pbn-dot { width: 7px; height: 7px; border-radius: 50%; background: #F59E0B; }

        /* alerts */
        .pbn-alert { display: flex; align-items: center; gap: 8px; font-size: 0.875rem; padding: 0.7rem 0.95rem; border-radius: 10px; }
        .pbn-alert--ok { background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.35); color: #10B981; }
        .pbn-alert--err { background: rgba(239,68,68,.12); border: 1px solid rgba(239,68,68,.35); color: #ef4444; }

        /* cards */
        .pbn-card { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
        .pbn-card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .pbn-title { margin: 0; font-size: 15px; font-weight: 700; color: var(--text1); display: flex; align-items: center; gap: 8px; }
        .pbn-muted { color: var(--text3); font-size: 12px; margin: 0; }
        .pbn-block { display: block; margin-top: 2px; }
        .pbn-note { margin: 0; font-size: 12px; color: var(--text3); line-height: 1.55; }
        .pbn-divider { height: 1px; background: var(--border); }

        .pbn-grid { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); gap: var(--space-lg, 20px); align-items: start; }
        .pbn-col { display: flex; flex-direction: column; gap: var(--space-lg, 20px); min-width: 0; }
        .pbn-two { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 1100px) { .pbn-grid { grid-template-columns: 1fr; } }
        @media (max-width: 640px) { .pbn-two { grid-template-columns: 1fr; } }

        /* fields */
        .pbn-field { display: flex; flex-direction: column; gap: 6px; }
        .pbn-label-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        .pbn-label { font-size: 13px; font-weight: 600; color: var(--text1); }
        .pbn-input {
          width: 100%; padding: 9px 12px; font: inherit; font-size: 13px; color: var(--text1);
          background: var(--bg-raised, #fff); border: 1px solid var(--border); border-radius: var(--radius-md, 8px);
          outline: none; transition: border-color .15s ease, box-shadow .15s ease;
        }
        .pbn-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79,70,229,.15); }
        .pbn-textarea { resize: vertical; min-height: 84px; line-height: 1.5; }
        .pbn-error { display: flex; align-items: center; gap: 5px; margin: 0; color: #ef4444; font-size: 12px; }

        .pbn-chips { display: flex; gap: 6px; flex-wrap: wrap; }
        .pbn-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgba(79,70,229,.1); color: var(--accent); }

        .pbn-link-btn {
          display: inline-flex; align-items: center; gap: 5px; padding: 0; border: 0; background: none;
          font: inherit; font-size: 12px; font-weight: 600; color: var(--accent); cursor: pointer;
        }
        .pbn-link-btn:hover { text-decoration: underline; }
        .pbn-danger { color: #ef4444; }

        /* dropzone */
        .pbn-drop {
          border: 1.5px dashed var(--border-active, var(--border)); border-radius: var(--radius-md, 10px);
          background: var(--bg-raised, #fafafa); padding: 10px; cursor: pointer; outline: none;
          transition: border-color .15s ease, background .15s ease;
        }
        .pbn-drop:hover, .pbn-drop:focus-visible, .pbn-drop.is-over { border-color: var(--accent); background: rgba(79,70,229,.05); }
        .pbn-drop.has-error { border-color: #ef4444; }
        .pbn-drop-empty { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 22px 12px; font-size: 13px; color: var(--text2); text-align: center; }
        .pbn-drop-empty small { color: var(--text3); font-size: 12px; }
        .pbn-drop-icon { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: rgba(79,70,229,.1); color: var(--accent); margin-bottom: 4px; }
        .pbn-drop-thumb { position: relative; width: 100%; border-radius: 8px; overflow: hidden; background: #e5e7eb; }
        .pbn-drop-thumb img { width: 100%; height: auto; max-height: 260px; object-fit: contain; display: block; }
        .pbn-new-tag { position: absolute; left: 8px; bottom: 8px; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: rgba(16,185,129,.9); color: #fff; }

        .pbn-file-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-height: 20px; }
        .pbn-file-name { font-size: 12px; color: var(--text1); max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
        .pbn-file-size { font-size: 12px; color: var(--text3); }

        /* colors */
        .pbn-color { display: flex; align-items: center; gap: 10px; }
        .pbn-color input[type="color"] {
          width: 42px; height: 38px; padding: 3px; cursor: pointer; flex-shrink: 0;
          border: 1px solid var(--border); border-radius: var(--radius-md, 8px); background: var(--bg-raised, #fff);
        }
        .pbn-hex { max-width: 120px; font-family: var(--font-mono, monospace); text-transform: uppercase; }

        /* switch */
        .pbn-switch-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-top: 4px; }
        .pbn-switch {
          position: relative; width: 44px; height: 24px; flex-shrink: 0; padding: 0; border: 0; cursor: pointer;
          border-radius: 999px; background: var(--border-active, #cbd5e1); transition: background .2s ease;
        }
        .pbn-switch span { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.3); transition: transform .2s ease; }
        .pbn-switch.is-on { background: #10B981; }
        .pbn-switch.is-on span { transform: translateX(20px); }

        /* preview tabs */
        .pbn-tabs { display: inline-flex; padding: 3px; gap: 2px; border: 1px solid var(--border); border-radius: 10px; background: var(--bg-raised, #f3f4f6); }
        .pbn-tabs button {
          display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border: 0; border-radius: 8px;
          background: transparent; color: var(--text2); font: inherit; font-size: 12px; font-weight: 600; cursor: pointer;
        }
        .pbn-tabs button.is-on { background: var(--accent); color: #fff; }

        /* ── drag & drop stage (same units as the website: x/y %, font-size in cqw) ── */
        .pbn-stage {
          position: relative; width: 100%; overflow: hidden; border-radius: 10px; background: #e5e7eb;
          container-type: inline-size; user-select: none; touch-action: none;
        }
        .pbn-stage.is-mobile { max-width: 100%; border-radius: 14px; box-shadow: 0 10px 30px rgba(0,0,0,.12); }
        .pbn-stage img { display: block; width: 100%; height: auto; pointer-events: none; }
        .pbn-stage-empty { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #6b7280; font-size: 13px; }

        .pbn-block {
          position: absolute; transform: translate(-50%, -50%);
          font-family: 'Poppins', sans-serif; font-weight: 500; line-height: 1.5; white-space: nowrap;
          color: var(--pb-text, #fff); cursor: grab; touch-action: none; outline: none;
          padding: 2px 6px; border-radius: 6px; border: 1.5px dashed transparent;
        }
        .pbn-block:hover { border-color: rgba(255,255,255,.7); background: rgba(0,0,0,.12); }
        .pbn-block.is-selected { border-color: #6366F1; background: rgba(99,102,241,.18); }
        .pbn-block.is-dragging { cursor: grabbing; border-style: solid; }
        .pbn-hl { color: var(--pb-hl, #D2AE4F); }

        .pbn-guide { position: absolute; pointer-events: none; background: rgba(99,102,241,.8); }
        .pbn-guide-v { top: 0; bottom: 0; left: 50%; width: 1px; }
        .pbn-guide-h { left: 0; right: 0; top: 50%; height: 1px; }

        .pbn-phone-bg { display: flex; justify-content: center; padding: 20px; border-radius: 10px; background: var(--bg-raised, #f3f4f6); }

        /* layout controls */
        .pbn-blk { display: flex; flex-direction: column; gap: 12px; padding: 12px; border: 1px solid var(--border); border-radius: 12px; cursor: default; }
        .pbn-blk.is-selected { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
        .pbn-blk-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pbn-blk-lbl { width: 54px; font-size: 12px; color: var(--text3); }
        .pbn-blk-row input[type="range"] { flex: 1; min-width: 80px; accent-color: var(--accent, #4F46E5); }
        .pbn-blk-val { width: 30px; font-size: 12px; text-align: right; color: var(--text1); font-family: var(--font-mono, monospace); }
        .pbn-seg { display: inline-flex; padding: 2px; gap: 2px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-raised, #f3f4f6); }
        .pbn-seg button { display: grid; place-items: center; width: 30px; height: 26px; border: 0; border-radius: 6px; background: transparent; color: var(--text2); cursor: pointer; }
        .pbn-seg button.is-on { background: var(--accent); color: #fff; }
        .pbn-xy { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: var(--text3); }
        .pbn-xy input {
          width: 62px; padding: 5px 6px; font: inherit; font-size: 12px; color: var(--text1);
          background: var(--bg-raised, #fff); border: 1px solid var(--border); border-radius: 6px; outline: none;
        }
        .pbn-xy input:focus { border-color: var(--accent); }
      `}</style>
    </div>
  )
}